import React, { useEffect, useState, useRef } from "react";
import "../CustomStyle.css";
import { Spinner, Alert } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {
  constructAndReturnValidationErrorMessage,
  validateRequiredFields,
  buildQueryParams,
  renderPdf,
  formatPhoneNumber
} from "../utils/PreviewUtils";
import { saveCustomizationToDB, generatePreviewPdf } from "../utils/api";
import TemplateSelector from "../components/TemplateSelector";
import PdfPreviewModal from "../components/PdfPreviewModal";
import ReusableFormWrapper from "../components/ReusableFormWrapper";
import PageHelmet from "../components/PageHelmet";

const ProofTemplate = ({ theme, userRole, bearerToken }) => {
  const [state, setState] = useState({
    selectedTemplate: "",
    templateConfig: null,
    validationErrors: {},
    pdfBinaryData: {},
    isModalOpen: false,
    templateName: "",
    isLoading: false,
    pdfImageSrc: null,
    error: "",
    successMessage: "",
    pdfError: null,
    initialMessage: "Type of Template determines the content & font.",
  });
  const [lastGeneratedParams, setLastGeneratedParams] = useState({});

  const [formData, setFormData] = useState({
    "Agent Letter - Typed": {
      prospectsQueryParams: {
        alias: "Mark & Kristina",
        qrCodeContent: "https://craigproctor.com/coaching/about-memberships",
        id: "1",
      },
      customerQueryParams: {
        realtor_info: "John Doe",
        sigline1: "John Doe, Broker & Owner",
        sigline2: "Your company name",
        sigline3: "CA BRE Lic#: 02184575",
        sigline4: "johndoe@gmail.com",
        cta1: "John",
        cta2: "xxx-xxx-xxxx",
        signature: "John Doe",
      },
      campaignQueryParams: {
        city: "San Ramon",
        buyer_names: "Bob and Mary",
      },
    },
    "Agent Letter - Biro": {
      prospectsQueryParams: {
        alias: "Mark & Kristina",
        qrCodeContent: "https://craigproctor.com/coaching/about-memberships",
        id: "1",
      },
      customerQueryParams: {
        realtor_info: "Jane Doe",
        sigline1: "Jane Doe, Broker & Owner",
        sigline2: "Your company name",
        sigline3: "CA BRE Lic#: 02184576",
        sigline4: "janedoe@gmail.com",
        cta1: "Jane",
        cta2: "xxx-xxx-xxxx",
        signature: "Jane Doe",
      },
      campaignQueryParams: {
        city: "San Francisco",
        buyer_names: "Alice and Bob",
      },
    },
    "Envelope #10": {
      prospectsQueryParams: {
        alias: "Mark Heinman & Kristina ",
        address1: "5505 Farnam St",
        city: "Omaha",
        state: "NE",
        zip: "68132",
        id: "1",
      },
      customerQueryParams: {
        return_address_line1: " ",
        return_address_line2: " ",
      },
    },
    "Cash Offer - Biro":{
      prospectsQueryParams: {
        alias: "Mark & Kristina",
        qrCodeContent: "https://craigproctor.com/coaching/about-memberships",
        id: "1",
      },
      customerQueryParams: {
        realtor_info: "Jane Doe",
        sigline1: "Jane Doe, Broker & Owner",
        sigline2: "Your company name",
        sigline3: "CA BRE Lic#: 02184576",
        sigline4: "janedoe@gmail.com",
        cta1: "Jane",
        cta2: "xxx-xxx-xxxx",
        signature: "Jane Doe",
      }

    },
     "Cash Offer - Typed":{
      prospectsQueryParams: {
        alias: "Mark & Kristina",
        qrCodeContent: "https://craigproctor.com/coaching/about-memberships",
        id: "1",
      },
      customerQueryParams: {
        realtor_info: "Jane Doe",
        sigline1: "Jane Doe, Broker & Owner",
        sigline2: "Your company name",
        sigline3: "CA BRE Lic#: 02184576",
        sigline4: "janedoe@gmail.com",
        cta1: "Jane",
        cta2: "xxx-xxx-xxxx",
        signature: "Jane Doe",
      }
    }
  });

  const fieldRefs = useRef({
    alias: null,
    address1: null,
    city: null,
    state: null,
    zip: null,
    id: null,
    qrCodeContent: null,
    realtor_info: null,
    sigline1: null,
    sigline4: null,
    cta1: null,
    cta2: null,
    buyer_names: null,
  });

  const scrollToFirstError = (validationErrors) => {
    const firstErrorField = Object.keys(validationErrors)[0];
    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      fieldRefs[firstErrorField].current.scrollIntoView({ behavior: "smooth" });
      fieldRefs[firstErrorField].current.focus();
    }
  };

  const handleInputChange = (e, section, key) => {
    const value = e.target.value;

    setFormData((prevState) => {
         if(key === "cta2"){
        const formattedPhone = formatPhoneNumber(value);
        return {
          ...prevState,
          [state.selectedTemplate]: {
            ...prevState[state.selectedTemplate],
            [section]: {
              ...(prevState[state.selectedTemplate]?.[section] || {}),
              [key]: formattedPhone,
            },
          },
        }
        }else{
            return {
              ...prevState,
              [state.selectedTemplate]: {
                ...prevState[state.selectedTemplate],
                [section]: {
                  ...(prevState[state.selectedTemplate]?.[section] || {}),
                  [key]: value,
                },
              },
            };
        }
    });
  };

  const handleSelect = (e) => {
    setState((prevState) => ({
      ...prevState,
      initialMessage: "",
      validationErrors: {},
      selectedTemplate: e.target.value,
      pdfError: null,
    }));
  };

  useEffect(() => {
    fetch("/templateconfig.json")
      .then((response) => response.json())
      .then((data) => {
        setState((prevState) => ({ ...prevState, templateConfig: data }));
      })
      .catch((error) => 
        setState((prevState) => ({ ...prevState, templateConfig: null, error: "Error fetching template config" }))
      );
  }, []);

  const trimTemplateData = (templateData) => {
    const trimmed = {};
    for (const sectionKey in templateData) {
      trimmed[sectionKey] = {};
      for (const fieldKey in templateData[sectionKey]) {
        const value = templateData[sectionKey][fieldKey];
        trimmed[sectionKey][fieldKey] =
          typeof value === "string" ? value.trim() : value;
      }
    }
    return trimmed;
  };

  const handleGeneratePreview = async () => {
    const templateData = formData[state.selectedTemplate];

    if(templateData.customerQueryParams && templateData.customerQueryParams.realtor_info)
      {
        templateData.customerQueryParams.signature = templateData.customerQueryParams.realtor_info;
      }

    const trimmedSection = trimTemplateData(templateData);
    const updatedFormData = {
      ...formData,
      [state.selectedTemplate]: trimmedSection,
    };
    setFormData(updatedFormData);
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: null,
      pdfError: null,
      successMessage: null,
    }));
    if (!state.templateConfig) {
      return;
    }
    const queryParams = buildQueryParams(
      state.selectedTemplate,
      updatedFormData
    );
    setState((prevState) => ({
      ...prevState,
      validationErrors: {},
    }));
    const missingFields = await validateRequiredFields(
      queryParams,
      state.templateConfig
    );

    if (Object.keys(missingFields).length > 0) {
      setState((prevState) => ({
        ...prevState,
        validationErrors: missingFields,
        isLoading: false,
      }));

      scrollToFirstError(missingFields); // Pass errors directly to scroll to the first one
      return;
    }
    const currentParamsString = JSON.stringify(queryParams);
    const lastParamsString = JSON.stringify(
      lastGeneratedParams[state.selectedTemplate] || {}
    );

    if (currentParamsString === lastParamsString) {
      const existingPdf = state.pdfBinaryData[state.selectedTemplate];
      if (existingPdf) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isModalOpen: true,
          pdfBinaryData: {
            ...prevState.pdfBinaryData,
            [state.selectedTemplate]: existingPdf,
          },
        }));
      }
    } else {
     
      try {
        const newPdfContent = await generatePreviewPdf(queryParams,bearerToken);
        if (!newPdfContent.success) {
          setState((prevState) => ({
            ...prevState,
            pdfError: newPdfContent.message,
            isLoading: false,
          }));
          return;
        }
        setState((prevState) => ({
          ...prevState,
          pdfBinaryData: {
            ...prevState.pdfBinaryData,
            [state.selectedTemplate]: newPdfContent.data,
          },
        }));
        setLastGeneratedParams((prev) => ({
          ...prev,
          [state.selectedTemplate]: queryParams,
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          pdfError: error.message,
          isLoading: false,
        }));
      }
    }
    return;
  };

  useEffect(() => {
    const renderPdfPreview = async () => {
      if (!state.pdfBinaryData[state.selectedTemplate]) return;

      try {
        const pdfImages = await renderPdf(
          state.pdfBinaryData[state.selectedTemplate]
        );
        setState((prevState) => ({
          ...prevState,
          pdfImageSrc: pdfImages,
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          pdfError: "Error rendering PDF preview"
        }));
      }
    };
    renderPdfPreview();
  }, [state.pdfBinaryData]);

  useEffect(() => {
    if (state.pdfBinaryData && Object.keys(state.pdfBinaryData).length > 0) {
      setState((prevState) => ({
        ...prevState,
        isModalOpen: true,
        isLoading: false,
      }));
    }
  }, [state.pdfBinaryData]);

  // function for mui modal  close
  const handleClose = () =>
    setState((prevState) => ({ ...prevState, isModalOpen: false }));

  // function to save template name
  const handleTemplateSave = async () => {
    setState((prevState) => ({
      ...prevState,
      error: null,
      successMessage: null,
      templateName: "",
    }));

    const trimmedTemplateName = state.templateName?.trim();

    if (!trimmedTemplateName) {
      setState((prevState) => ({
        ...prevState,
        error: "Need a unique name to 'Save Template'",
      }));
      return;
    }
    if (trimmedTemplateName === "") {
      setState((prevState) => ({
        ...prevState,
        error: "Template name cannot be an empty",
      }));
      return;
    }

    try {
      const data = {
        prospect: formData[state.selectedTemplate].prospectsQueryParams,
        customer: formData[state.selectedTemplate].customerQueryParams,
        campaign: formData[state.selectedTemplate].campaignQueryParams || null,
        template: state.selectedTemplate,
        name: trimmedTemplateName,
      };
      setState((prevState) => ({
        ...prevState,
        successMessage: null,
        error: null,
      }));
      const response = await saveCustomizationToDB(data, bearerToken);
      switch (response.status) {
        case 201:
          setState((prevState) => ({
            ...prevState,
            successMessage:
              "Template saved successfully",
            templateName: "",
          }));
          break;
        case 400:
          setState((prevState) => ({
            ...prevState,
            error:
              "The operation you attempted has failed. Please contact support.",
          }));
          break;
        case 409:
          const message = `A template already exists for the given name ${state.templateName}. Please choose another name for this template.`;
          setState((prevState) => ({
            ...prevState,
            error: message,
          }));
          break;
        case 422:
          const decoder = new TextDecoder("utf-8");
          const responseText = decoder.decode(response.data);

          const responseJson = JSON.parse(responseText);
          const rawMessage = responseJson.message;
          const messageContent =
            "Your attempt to create a custom template failed due to invalid inputs that were found while validating it";
          const constructErrorMesage = constructAndReturnValidationErrorMessage(
            rawMessage,
            messageContent
          );
          setState((prevState) => ({
            ...prevState,
            error: constructErrorMesage,
          }));

        default:
          setState((prevState) => ({
            ...prevState,
            error:
              "The operation you attempted has failed. Please contact support.",
          }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        error: error.message,
      }));
    }
  };

  const handleDownloadPdf = () => {
    const arrayBuffer = state.pdfBinaryData[state.selectedTemplate];
    if (arrayBuffer) {
      try {
        const blob = new Blob([arrayBuffer], { type: "application/pdf" });
        const link = document.createElement("a");

        link.href = URL.createObjectURL(blob);
        link.download = `${state.selectedTemplate}_preview`;
        link.click();
      } catch (error) {
        throw new Error("Error downloading PDF " );
      }
    }
  };

  return userRole === "customer" ? (
    <div className="dashboard-container">
      <PageHelmet
        title="Proof Template"
        description="Choose and Save templates with TraQR."
        url={`${process.env.REACT_APP_REDIRECT_URI}/proof_template`}
      />
      <h1 className="main-heading">TraQR - Create & Proof Your Template</h1>
      <TemplateSelector
        selectedTemplate={state.selectedTemplate}
        handleSelect={handleSelect}
        initialMessage={state.initialMessage}
      />
      {state.selectedTemplate && (
        <div className="proof-form-container">
          <ReusableFormWrapper
            selectedTemplate={state.selectedTemplate}
            formData={formData}
            handleInputChange={handleInputChange}
            validationErrors={state.validationErrors}
            fieldRefs={fieldRefs}
          />
          <Button
            variant="dark"
            onClick={() => {
              handleGeneratePreview();
            }}
            className="generate-button"
          >
            Preview Template
          </Button>
        </div>
      )}
      {state.isLoading &&
        Object.keys(state.validationErrors || {}).length == 0 && (
          <div className="template-loading-container">
            <Spinner animation="border" variant="primary" />
            <p>Generating {state.selectedTemplate} preview</p>
          </div>
        )}
      {state.pdfError && (
        <Alert variant="danger" style={{ margin: "10px" }}>
          {state.pdfError}
        </Alert>
      )}

      <PdfPreviewModal
        isModalOpen={state.isModalOpen}
        handleClose={handleClose}
        pdfImageSrc={state.pdfImageSrc}
        selectedTemplate={state.selectedTemplate}
        handleDownloadPdf={() =>
          handleDownloadPdf(state.pdfBinaryData, state.selectedTemplate)
        }
        theme={theme}
        error={state.error}
        successMessage={state.successMessage}
        templateName={state.templateName}
        setTemplateName={(name) =>
          setState((prevState) => ({ ...prevState, templateName: name }))
        }
        handleTemplateSave={handleTemplateSave}
      />
    </div>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
};
export default ProofTemplate;
